import type { ExportJobDetail } from "@/types/api";
import { formatDateIsoUtcFromEpoch } from "@/utils/format/date";

export interface ExportFilterDetailRow {
  label: string;
  value: string;
}

export interface ExportFilterDisplay {
  /** Primary line in list view (e.g. date range or selection). */
  headline: string;
  /** Secondary line (customers, status, etc.). */
  subline: string | null;
  /** Structured rows for detail view. */
  rows: ExportFilterDetailRow[];
}

type FilterSnapshot = {
  customer_ids?: string[];
  statuses?: string[];
  created_from?: number;
  created_to?: number;
  due_from?: number;
  due_to?: number;
  amount_due_min?: number;
  amount_due_max?: number;
};

function parseFilterSnapshot(job: ExportJobDetail): FilterSnapshot | null {
  const v = job.filter_snapshot_json;
  if (v == null) {
    return null;
  }
  try {
    const raw = typeof v === "string" ? JSON.parse(v) : v;
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
      return null;
    }
    return raw as FilterSnapshot;
  } catch {
    return null;
  }
}

function formatEpochDate(epoch: number): string {
  return formatDateIsoUtcFromEpoch(epoch);
}

function formatDateRange(from?: number, to?: number): string | null {
  if (from == null && to == null) {
    return null;
  }
  if (from != null && to != null) {
    return `${formatEpochDate(from)} – ${formatEpochDate(to)}`;
  }
  if (from != null) {
    return `From ${formatEpochDate(from)}`;
  }
  return `Until ${formatEpochDate(to!)}`;
}

function formatCustomers(snapshot: FilterSnapshot): string {
  const ids = snapshot.customer_ids;
  if (!ids?.length) {
    return "All customers";
  }
  if (ids.length === 1) {
    return "1 customer selected";
  }
  return `${ids.length} customers selected`;
}

function formatStatuses(snapshot: FilterSnapshot): string {
  const statuses = snapshot.statuses?.filter(Boolean);
  if (!statuses?.length) {
    return "All statuses";
  }
  return statuses
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " "))
    .join(", ");
}

function formatAmountRange(snapshot: FilterSnapshot): string | null {
  const min = snapshot.amount_due_min;
  const max = snapshot.amount_due_max;
  if (min == null && max == null) {
    return null;
  }
  const fmt = (minor: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(minor / 100);
  if (min != null && max != null) {
    return `${fmt(min)} – ${fmt(max)}`;
  }
  if (min != null) {
    return `At least ${fmt(min)}`;
  }
  return `Up to ${fmt(max!)}`;
}

function buildRowsFromSnapshot(snapshot: FilterSnapshot): ExportFilterDetailRow[] {
  const rows: ExportFilterDetailRow[] = [];

  const created = formatDateRange(snapshot.created_from, snapshot.created_to);
  if (created) {
    rows.push({ label: "Invoice created", value: created });
  }

  const due = formatDateRange(snapshot.due_from, snapshot.due_to);
  if (due) {
    rows.push({ label: "Due date", value: due });
  }

  rows.push({ label: "Customers", value: formatCustomers(snapshot) });
  rows.push({ label: "Status", value: formatStatuses(snapshot) });

  const amount = formatAmountRange(snapshot);
  if (amount) {
    rows.push({ label: "Amount due", value: amount });
  }

  return rows;
}

function buildHeadlineFromRows(rows: ExportFilterDetailRow[]): {
  headline: string;
  subline: string | null;
} {
  const created = rows.find((r) => r.label === "Invoice created")?.value;
  const customers = rows.find((r) => r.label === "Customers")?.value;
  const status = rows.find((r) => r.label === "Status")?.value;
  const due = rows.find((r) => r.label === "Due date")?.value;

  if (created) {
    const subParts = [customers, status].filter(Boolean);
    return {
      headline: created,
      subline: subParts.length > 0 ? subParts.join(" · ") : null,
    };
  }
  if (due) {
    return {
      headline: due,
      subline: [customers, status].filter(Boolean).join(" · ") || null,
    };
  }
  const headline = [customers, status].filter(Boolean).join(" · ");
  return { headline: headline || "All invoices", subline: null };
}

function rowsFromFilterSummary(summary: string): ExportFilterDetailRow[] {
  return summary.split(" · ").map((part) => {
    const colon = part.indexOf(":");
    if (colon === -1) {
      return { label: "Criteria", value: part.trim() };
    }
    return {
      label: part.slice(0, colon).trim(),
      value: part.slice(colon + 1).trim(),
    };
  });
}

function displayFromFilterSummary(summary: string): ExportFilterDisplay {
  const rows = rowsFromFilterSummary(summary);
  const createdRow = rows.find((r) => r.label === "Created");
  const headline = createdRow
    ? createdRow.value
    : rows.map((r) => `${r.label}: ${r.value}`).join(" · ");
  const subline = createdRow
    ? rows
        .filter((r) => r.label !== "Created")
        .map((r) => `${r.label}: ${r.value}`)
        .join(" · ") || null
    : null;

  return {
    headline,
    subline,
    rows: rows.map((r) =>
      r.label === "Created"
        ? { label: "Invoice created", value: r.value }
        : r,
    ),
  };
}

function displayForManualSelection(job: ExportJobDetail): ExportFilterDisplay {
  const count = job.invoice_count;
  const noun = count === 1 ? "invoice" : "invoices";
  return {
    headline: "Manually selected invoices",
    subline: `${count} ${noun} chosen in the list`,
    rows: [
      {
        label: "How exported",
        value: `${count} ${noun} selected manually (not filter-based)`,
      },
    ],
  };
}

/** Finance-friendly filter criteria for list + detail views. */
export function buildExportFilterDisplay(job: ExportJobDetail): ExportFilterDisplay {
  const snapshot = parseFilterSnapshot(job);
  if (snapshot) {
    const rows = buildRowsFromSnapshot(snapshot);
    const { headline, subline } = buildHeadlineFromRows(rows);
    return { headline, subline, rows };
  }

  const summary = job.filter_summary?.trim();
  if (summary) {
    return displayFromFilterSummary(summary);
  }

  if (job.source === "from_filters") {
    return {
      headline: "Filter-based export",
      subline: "Criteria unavailable",
      rows: [{ label: "Note", value: "Filter details were not saved for this job." }],
    };
  }

  if (job.source === "selection" || job.invoice_count > 0) {
    return displayForManualSelection(job);
  }

  return {
    headline: "—",
    subline: null,
    rows: [],
  };
}
